"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setCurrencyRateRequestSchema = exports.currencyRateDaySchema = exports.currencyRateCellSchema = exports.CURRENCY_RATE_SOURCES = exports.CURRENCY_CATALOGUE = exports.setDefaultCurrencyRequestSchema = exports.updateCurrencyRequestSchema = exports.createCurrencyRequestSchema = exports.currencySettingsSchema = exports.workspaceCurrencySchema = exports.CURRENCY_SOURCES = void 0;
exports.fxRate = fxRate;
exports.fxConvert = fxConvert;
exports.formatMoney = formatMoney;
exports.formatRate = formatRate;
exports.rateBookOf = rateBookOf;
exports.rateOn = rateOn;
exports.fxRateOn = fxRateOn;
exports.fxConvertOn = fxConvertOn;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The currencies a workspace works in — C51, Phase 4.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Phase 4 prices work: a rate card in EUR, a cost in HUF, a project's finance tab totalling both.
 * So a conversion has to be a fact about the workspace rather than a number typed into a screen.
 *
 * ⚠️⚠️ **EVERY RATE IS AGAINST THE DEFAULT, AND THE MATRIX THE SCREEN DRAWS IS DERIVED.** `0126`
 * argues it at length; the consequence for a reader is one line — `rate(from, to) =
 * rateToBase[from] / rateToBase[to]` — and the guarantee is that no two cells can disagree.
 */
const codeSchema = zod_1.z.string().regex(/^[A-Z]{3}$/, 'a currency code is three upper-case letters');
/** Where the rates came from. `ecb` is Frankfurter, which quotes the ECB's daily reference set. */
exports.CURRENCY_SOURCES = ['manual', 'ecb'];
exports.workspaceCurrencySchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `currency_3f2a9c1e` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('currency'),
    code: codeSchema,
    name: zod_1.z.string(),
    /** As it is written beside a figure — `Ft`, `€`, `lei`. Not derivable from the code. */
    symbol: zod_1.z.string(),
    /**
     * ⚠️⚠️ **1 OF THIS = THIS MANY OF THE DEFAULT, AND A STRING BECAUSE `numeric` CROSSES THE DRIVER
     * AS ONE.** The same rule hours and money already follow. Parse before arithmetic — and parse
     * with the full scale, because `0.00252207` rounded to four places moves a million by hundreds.
     */
    rateToBase: zod_1.z.string(),
    /** ⚠️ The default's own rate is always `1` — pinned by a trigger, not by the caller. */
    isDefault: zod_1.z.boolean(),
    active: zod_1.z.boolean(),
    version: zod_1.z.number().int(),
});
/**
 * The workspace's whole money setting, which is what the screen reads: the list, plus where the
 * rates came from and when.
 *
 * ⚠️ `syncedAt` is `null` until a sync has run, which is exactly what the footer renders as
 * *"Még nem futott frissítés — a táblázat kézi értékeket mutat"*.
 */
exports.currencySettingsSchema = zod_1.z.object({
    currencies: zod_1.z.array(exports.workspaceCurrencySchema),
    source: zod_1.z.enum(exports.CURRENCY_SOURCES),
    syncedAt: zod_1.z.string().nullable(),
    /** The address the sync would call, so the screen can show it rather than describe it. */
    endpoint: zod_1.z.string(),
});
exports.createCurrencyRequestSchema = zod_1.z.object({
    code: codeSchema,
    name: zod_1.z.string().trim().min(1).max(60),
    symbol: zod_1.z.string().trim().min(1).max(8),
});
/**
 * ⚠️⚠️ **THE RATE IS WRITTEN IN EITHER DIRECTION, BECAUSE THE MATRIX LETS SOMEBODY TYPE IN EITHER
 * CELL.** `1 EUR = 396.5 HUF` and `1 HUF = 0.00252 EUR` are the same fact, and the screen offers
 * both boxes. The client sends which pair it edited; the server stores the one column.
 */
exports.updateCurrencyRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(60).optional(),
    symbol: zod_1.z.string().trim().min(1).max(8).optional(),
    active: zod_1.z.boolean().optional(),
    /** ⚠️ Positive and finite. Zero would make every figure in that currency infinite or nothing. */
    rateToBase: zod_1.z.number().positive().optional(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **Making one the default re-bases every other rate**, which is arithmetic rather than a
 * re-fetch: the stored column is *against the default*, so moving the default divides the lot by
 * the new one's old rate. The server does it; this shape only names the winner.
 */
exports.setDefaultCurrencyRequestSchema = zod_1.z.object({ code: codeSchema });
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE CATALOGUE THE PICKER OFFERS — twelve, not the ISO list.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's own `CUR_CATALOG`. A dropdown of 180 codes is a search box with no answer; these
 * are the ones a Hungarian delivery company actually invoices in, and the dialog lets somebody
 * type a code the list does not have — *"válassz a listából, vagy írd be kézzel"*.
 */
exports.CURRENCY_CATALOGUE = [
    { code: 'HUF', name: 'Magyar forint', symbol: 'Ft' },
    { code: 'EUR', name: 'Euró', symbol: '€' },
    { code: 'USD', name: 'Amerikai dollár', symbol: '$' },
    { code: 'GBP', name: 'Angol font', symbol: '£' },
    { code: 'CHF', name: 'Svájci frank', symbol: 'CHF' },
    { code: 'RON', name: 'Román lej', symbol: 'lei' },
    { code: 'PLN', name: 'Lengyel zloty', symbol: 'zł' },
    { code: 'CZK', name: 'Cseh korona', symbol: 'Kč' },
    { code: 'SEK', name: 'Svéd korona', symbol: 'kr' },
    { code: 'NOK', name: 'Norvég korona', symbol: 'kr' },
    { code: 'DKK', name: 'Dán korona', symbol: 'kr' },
    { code: 'JPY', name: 'Japán jen', symbol: '¥' },
];
/**
 * What one unit of `from` is worth in `to`.
 *
 * ⚠️⚠️ **ONE DIVISION, AND THAT IS THE WHOLE CONVERSION.** Both rates are against the default, so
 * the default cancels — which is why the matrix cannot contradict itself and why nothing has to
 * decide a direction. `null` when either currency is unknown to the workspace: a caller that
 * treated a missing rate as 1 would report a EUR figure as forints.
 */
function fxRate(from, to, rates) {
    if (from === to)
        return 1;
    const a = Number(rates[from]);
    const b = Number(rates[to]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0)
        return null;
    return a / b;
}
/**
 * An amount, converted.
 *
 * ⚠️ **Rounded to two places at the END, not per step.** Converting through a third currency and
 * rounding twice is how a total stops matching the sum of its rows.
 */
function fxConvert(amount, from, to, rates) {
    const rate = fxRate(from, to, rates);
    if (rate === null)
        return null;
    return Math.round(amount * rate * 100) / 100;
}
/**
 * A figure with its symbol, the way this product writes one.
 *
 * ⚠️ **The symbol leads for EUR, USD and GBP and trails for everything else** — the mockup's own
 * rule, and it is what a Hungarian reader expects: `€ 1 200` but `1 200 Ft`. Thin spaces group the
 * thousands because a comma is the decimal separator here.
 */
function formatMoney(amount, currency) {
    const rounded = Math.round(amount);
    const grouped = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const signed = rounded < 0 ? `−${grouped}` : grouped;
    return ['EUR', 'USD', 'GBP'].includes(currency.code)
        ? `${currency.symbol} ${signed}`
        : `${signed} ${currency.symbol}`;
}
/**
 * A rate, written with the precision it needs.
 *
 * ⚠️ **The scale follows the magnitude** — the mockup's own rule. `396,50` needs two places and
 * `0,002522` needs six; a fixed scale renders one of them as `0,00` and the reader cannot tell
 * whether the rate is missing or tiny.
 */
function formatRate(value) {
    const abs = Math.abs(value);
    const places = abs >= 100 ? 2 : abs >= 1 ? 4 : 6;
    return value.toFixed(places).replace('.', ',');
}
/* ── exchange rates by day — decisions.md FX-1 … FX-8, `0230` ─────────────────────────────── */
exports.CURRENCY_RATE_SOURCES = ['ecb', 'manual'];
/** A book from the latest rates and the dated rows, in any order. */
function rateBookOf(latest, rows) {
    const days = {};
    for (const row of rows) {
        const rate = Number(row.rate);
        if (!Number.isFinite(rate) || rate <= 0)
            continue;
        (days[row.code] ??= []).push([row.onDate, rate]);
    }
    for (const series of Object.values(days))
        series.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    return { days, latest };
}
/**
 * One currency's rate to the base on a day.
 *
 * ⚠️⚠️ **THE RULES, ONCE** — FX-3, FX-4, FX-6:
 *  - a day without a row takes the last earlier one (a weekend, a holiday, a day not yet synced);
 *  - a day before the first row takes the earliest one — history that was never backfilled
 *    converts at the oldest rate known rather than at nothing;
 *  - `on === null` is a future or undated figure and takes the latest (FX-4);
 *  - a currency with no rows at all takes `latest` — the base, which is 1, or a currency added
 *    before its first sync.
 */
function rateOn(book, code, on) {
    const series = book.days[code];
    if (series === undefined || series.length === 0) {
        const latest = Number(book.latest[code]);
        return Number.isFinite(latest) && latest > 0 ? latest : null;
    }
    if (on === null)
        return series[series.length - 1]?.[1] ?? null;
    let low = 0;
    let high = series.length - 1;
    let found = -1;
    while (low <= high) {
        const middle = (low + high) >> 1;
        const day = series[middle];
        if (day === undefined)
            break;
        if (day[0] <= on) {
            found = middle;
            low = middle + 1;
        }
        else {
            high = middle - 1;
        }
    }
    return (series[found === -1 ? 0 : found] ?? series[0])?.[1] ?? null;
}
/** How many `to` one `from` was worth on a day — `fxRate`, dated. Null when either is unknown. */
function fxRateOn(from, to, on, book) {
    if (from === to)
        return 1;
    const a = rateOn(book, from, on);
    const b = rateOn(book, to, on);
    if (a === null || b === null)
        return null;
    return a / b;
}
/** An amount in `to` at the day's rate, to the fillér. */
function fxConvertOn(amount, from, to, on, book) {
    const rate = fxRateOn(from, to, on, book);
    if (rate === null)
        return null;
    return Math.round(amount * rate * 100) / 100;
}
/** One currency's rate on one day, as the *Napi árfolyamok* table draws a cell. */
exports.currencyRateCellSchema = zod_1.z.object({
    value: zod_1.z.string(),
    source: zod_1.z.enum(exports.CURRENCY_RATE_SOURCES),
    /** What the bank quoted; null for a day it never quoted. A manual cell can be restored to it. */
    bankValue: zod_1.z.string().nullable(),
    version: zod_1.z.number().int(),
});
/** One banking day: every non-base currency's rate, `null` where that day has none. */
exports.currencyRateDaySchema = zod_1.z.object({
    date: zod_1.z.string(),
    rates: zod_1.z.record(zod_1.z.string(), exports.currencyRateCellSchema.nullable()),
});
/** Set one day's rate by hand (FX-5). `value` is base units per one of the currency. */
exports.setCurrencyRateRequestSchema = zod_1.z.object({
    value: zod_1.z.number().positive().max(1_000_000_000),
    version: zod_1.z.number().int().optional(),
});
//# sourceMappingURL=currency.js.map